import { useStanQuery } from '@/queries';
import { appointmentsApi } from '@/lib/api/appointments';
import { getLocalTimezone, QUERY_KEYS } from '@/lib';
import type { BlockedTimeEvent, Booking } from '../types/appointment.types';

interface UseBlockedTimesParams {
    timezone?: string;
    startDate?: string;
    endDate?: string;
}

interface UseBookingsParams {
    timezone?: string;
    startDate?: string;
    endDate?: string;
    bookingStatus?: number;
    page?: number;
    pageSize?: number;
}

/**
 * Query for fetching blocked times
 */
export function useBlockedTimesQuery({ timezone = getLocalTimezone(), startDate, endDate }: UseBlockedTimesParams) {
    return useStanQuery<{ data: BlockedTimeEvent[] }>(
        {
            queryKey: [QUERY_KEYS.BLOCKED_TIMES, timezone, startDate, endDate],
            queryFn: async () => {
                if (!startDate || !endDate) {
                    return { data: [] };
                }
                return await appointmentsApi.getBlockedTimes(timezone, startDate, endDate);
            },
            staleTime: 5 * 60 * 1000, // 5 minutes
            enabled: !!startDate && !!endDate,
            meta: { error: { sentryMessage: '[APPOINTMENTS] failed-to-fetch-blocked-times' } },
        },
        true
    );
}

/**
 * Query for fetching bookings
 */
export function useBookingsQuery({
    timezone = getLocalTimezone(),
    startDate,
    endDate,
    bookingStatus,
    page = 1,
    pageSize = 10,
}: UseBookingsParams) {
    return useStanQuery<{
        data: Booking[];
        metadata: {
            page: number;
            pageSize: number;
            currentPageCustomerCount: number;
            totalMeetings: number;
            totalPages: number;
            timezone: string;
            keywordFilter: string;
        };
    }>(
        {
            queryKey: [QUERY_KEYS.BOOKINGS, timezone, startDate, endDate, bookingStatus, page, pageSize],
            queryFn: async () => {
                return await appointmentsApi.getBookings({
                    timezone,
                    startDate,
                    endDate,
                    ...(bookingStatus !== undefined && { bookingStatus }),
                    page,
                    pageSize,
                });
            },
            staleTime: 5 * 60 * 1000, // 5 minutes
            meta: { error: { sentryMessage: '[APPOINTMENTS] failed-to-fetch-bookings' } },
        },
        true
    );
}
